import React, { useEffect, useState } from 'react';
import { Table, Label, Pagination, Form, Button, Segment, Grid, Dropdown, Popup } from 'semantic-ui-react';
import { API, showError, timestamp2string } from '../helpers';

const SOURCE_OPTIONS = [
  { key: 'all', text: '全部来源', value: '' },
  { key: 'dynamic_api', text: 'Dynamic API', value: 'dynamic_api' },
  { key: 'llm_query', text: 'LLM 查询', value: 'llm_query' },
  { key: 'search', text: '搜索', value: 'search' },
];

const ActionLogsTable = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchUserId, setSearchUserId] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const loadLogs = async (userId, page, source) => {
    setLoading(true);
    try {
      let url = '/api/admin/actions/logs';
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (userId) {
        url = `/api/user/${userId}/actions/logs`;
      }
      if (source) params.append('source', source);

      const res = await API.get(`${url}?${params.toString()}`);
      const responseData = res.data;
      const payload = responseData.data || responseData;
      setLogs(payload.logs || []);
      setTotal(payload.total || 0);
    } catch (error) {
      showError('加载日志失败: ' + error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadLogs(currentUserId, activePage, sourceFilter);
  }, [activePage, currentUserId, sourceFilter]);

  const handleSearch = () => {
    setCurrentUserId(searchUserId);
    setActivePage(1);
  };

  const handleClearSearch = () => {
    setSearchUserId('');
    setCurrentUserId('');
    setActivePage(1);
  };

  const onPaginationChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  const onSourceChange = (e, { value }) => {
    setSourceFilter(value);
    setActivePage(1);
  };

  const renderStatus = (status) => {
    return status === 0
      ? <Label color='green'>成功</Label>
      : <Label color='red'>失败</Label>;
  };

  const renderSource = (source) => {
    const map = { dynamic_api: 'Dynamic API', llm_query: 'LLM 查询', search: '搜索' };
    const colorMap = { dynamic_api: 'purple', llm_query: 'blue', search: 'teal' };
    return <Label color={colorMap[source] || 'grey'}>{map[source] || source || '-'}</Label>;
  };

  // 渲染用户列：优先显示 username，并提供跳转到用户编辑页的链接
  const renderUser = (log) => {
    const displayName = log.username || String(log.user_id);
    const link = `/user/edit/${log.user_id}`;
    return (
      <Popup
        content={`用户 ID: ${log.user_id}`}
        trigger={
          <a href={link} target='_blank' rel='noopener noreferrer' style={{ fontWeight: log.username ? 'normal' : 'bold' }}>
            {displayName}
          </a>
        }
        size='small'
      />
    );
  };

  // 渲染 Token 列：优先显示 token_name，ID 作为 tooltip
  const renderToken = (log) => {
    if (!log.token_id) return '-';
    const displayName = log.token_name || `#${log.token_id}`;
    return (
      <Popup
        content={`Token ID: ${log.token_id}`}
        trigger={<span style={{ cursor: 'default' }}>{displayName}</span>}
        size='small'
      />
    );
  };

  return (
    <>
      <Segment>
        <Form>
          <Grid columns={3}>
            <Grid.Column width={6}>
              <Form.Input
                placeholder='输入用户 ID 搜索'
                value={searchUserId}
                onChange={(e) => setSearchUserId(e.target.value)}
                action={
                  <>
                    <Button primary onClick={handleSearch}>搜索</Button>
                    <Button onClick={handleClearSearch}>清空</Button>
                  </>
                }
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Dropdown
                selection
                options={SOURCE_OPTIONS}
                value={sourceFilter}
                onChange={onSourceChange}
                placeholder='筛选来源'
                style={{ width: '100%' }}
              />
            </Grid.Column>
          </Grid>
        </Form>
      </Segment>

      <Table basic='very'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>时间</Table.HeaderCell>
            <Table.HeaderCell>用户</Table.HeaderCell>
            <Table.HeaderCell>Token</Table.HeaderCell>
            <Table.HeaderCell>来源</Table.HeaderCell>
            <Table.HeaderCell>Dynamic API</Table.HeaderCell>
            <Table.HeaderCell>执行 ID</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
            <Table.HeaderCell>Tokens 使用</Table.HeaderCell>
            <Table.HeaderCell>执行时长</Table.HeaderCell>
            <Table.HeaderCell>状态</Table.HeaderCell>
            <Table.HeaderCell>错误信息</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {logs.map((log) => (
            <Table.Row key={log.id}>
              <Table.Cell>{timestamp2string(log.created_at)}</Table.Cell>
              <Table.Cell>{renderUser(log)}</Table.Cell>
              <Table.Cell>{renderToken(log)}</Table.Cell>
              <Table.Cell>{renderSource(log.source)}</Table.Cell>
              <Table.Cell>
                <div>{log.dynamic_api_name || '-'}</div>
                <div style={{ fontSize: '0.9em', color: '#888' }}>{log.dynamic_api_id || ''}</div>
              </Table.Cell>
              <Table.Cell>
                <div style={{ fontSize: '0.85em', fontFamily: 'monospace' }}>
                  {log.execution_id ? log.execution_id.substring(0, 8) + '...' : '-'}
                </div>
              </Table.Cell>
              <Table.Cell>{log.action_count}</Table.Cell>
              <Table.Cell>{log.tokens_used || 0}</Table.Cell>
              <Table.Cell>{log.duration_ms ? `${log.duration_ms}ms` : '-'}</Table.Cell>
              <Table.Cell>{renderStatus(log.status)}</Table.Cell>
              <Table.Cell>
                {log.error_message ? (
                  <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.error_message}
                  </div>
                ) : '-'}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='11'>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>共 {total} 条记录</div>
                <Pagination
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  totalPages={Math.max(1, Math.ceil(total / pageSize))}
                  siblingRange={1}
                  firstItem={null}
                  lastItem={null}
                />
              </div>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default ActionLogsTable;
